<template>
  <div class="modal is-active crop-photo-modal">
    <div class="modal-background" @click="!saving && $emit('close')" />
    <div class="modal-content p-4">
      <div class="box">
        <div class="is-flex is-justify-content-space-between is-align-items-center mb-4">
          <p class="has-text-weight-semibold">Ajustar foto de perfil</p>
          <button class="delete" :disabled="saving" @click="$emit('close')" />
        </div>

        <div
          class="crop-area"
          ref="cropArea"
          @mousedown.prevent="onDragStart"
          @touchstart.prevent="onDragStart"
        >
          <img
            ref="imgEl"
            :src="src"
            class="crop-image"
            :style="imgStyle"
            draggable="false"
            @load="onImgLoad"
          />
          <div class="crop-circle" />
        </div>

        <div class="is-flex is-align-items-center mt-4 px-2">
          <input
            type="range"
            class="zoom-slider"
            :min="1"
            :max="3"
            :step="0.01"
            v-model.number="zoom"
          />
        </div>

        <p v-if="errorMessage" class="has-text-danger is-size-7 mt-3">{{ errorMessage }}</p>

        <div class="is-flex is-justify-content-space-between mt-5">
          <button type="button" class="button" :disabled="saving" @click="$emit('close')">
            Cancelar
          </button>
          <button
            type="button"
            class="button is-warning"
            :disabled="saving || !loaded"
            @click="save"
          >
            <span v-if="saving">Salvando...</span>
            <span v-else>Salvar foto</span>
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onUnmounted, watch } from "vue";

const CROP_SIZE = 280;
const OUTPUT_SIZE = 600;

export default {
  emits: ["close"],

  props: {
    src: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const cropArea = ref(null);
    const imgEl = ref(null);
    const offsetX = ref(0);
    const offsetY = ref(0);
    const zoom = ref(1);
    const baseW = ref(0);
    const baseH = ref(0);
    const loaded = ref(false);
    const saving = ref(false);
    const errorMessage = ref(null);

    const csrfToken =
      document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";

    const imgStyle = computed(() => ({
      position: "absolute",
      width: `${baseW.value * zoom.value}px`,
      height: `${baseH.value * zoom.value}px`,
      top: "50%",
      left: "50%",
      transform: `translate(calc(-50% + ${offsetX.value}px), calc(-50% + ${offsetY.value}px))`,
      cursor: "grab",
      userSelect: "none",
      WebkitUserSelect: "none",
    }));

    const clampOffset = () => {
      const maxX = (baseW.value * zoom.value - CROP_SIZE) / 2;
      const maxY = (baseH.value * zoom.value - CROP_SIZE) / 2;
      offsetX.value = Math.max(-maxX, Math.min(maxX, offsetX.value));
      offsetY.value = Math.max(-maxY, Math.min(maxY, offsetY.value));
    };

    watch(zoom, () => {
      clampOffset();
    });

    const onImgLoad = () => {
      const { naturalWidth: nw, naturalHeight: nh } = imgEl.value;
      const aspect = nw / nh;

      if (aspect > 1) {
        baseH.value = CROP_SIZE;
        baseW.value = CROP_SIZE * aspect;
      } else {
        baseW.value = CROP_SIZE;
        baseH.value = CROP_SIZE / aspect;
      }

      zoom.value = 1;
      offsetX.value = 0;
      offsetY.value = 0;
      loaded.value = true;
    };

    let cleanupDrag = null;

    const onDragStart = (e) => {
      const point = e.touches ? e.touches[0] : e;
      const startX = point.clientX;
      const startY = point.clientY;
      const startOffsetX = offsetX.value;
      const startOffsetY = offsetY.value;

      const onMove = (ev) => {
        const pt = ev.touches ? ev.touches[0] : ev;
        offsetX.value = startOffsetX + (pt.clientX - startX);
        offsetY.value = startOffsetY + (pt.clientY - startY);
        clampOffset();
      };

      const onEnd = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onEnd);
        window.removeEventListener("touchmove", onMove);
        window.removeEventListener("touchend", onEnd);
        cleanupDrag = null;
      };

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onEnd);
      window.addEventListener("touchmove", onMove, { passive: true });
      window.addEventListener("touchend", onEnd);
      cleanupDrag = onEnd;
    };

    onUnmounted(() => {
      if (cleanupDrag) cleanupDrag();
    });

    const save = () => {
      const img = imgEl.value;
      if (!img) return;

      saving.value = true;
      errorMessage.value = null;

      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        errorMessage.value = "Não foi possível salvar a foto. Por favor, tente novamente.";
        saving.value = false;
        return;
      }

      const dispW = baseW.value * zoom.value;
      const dispH = baseH.value * zoom.value;
      const imgLeft = CROP_SIZE / 2 - dispW / 2 + offsetX.value;
      const imgTop = CROP_SIZE / 2 - dispH / 2 + offsetY.value;
      const scaleX = img.naturalWidth / dispW;
      const scaleY = img.naturalHeight / dispH;

      ctx.drawImage(
        img,
        -imgLeft * scaleX,
        -imgTop * scaleY,
        CROP_SIZE * scaleX,
        CROP_SIZE * scaleY,
        0,
        0,
        OUTPUT_SIZE,
        OUTPUT_SIZE,
      );

      canvas.toBlob((blob) => {
        if (!blob) {
          errorMessage.value = "Não foi possível salvar a foto. Por favor, tente novamente.";
          saving.value = false;
          return;
        }

        const file = new File([blob], "avatar.jpg", { type: "image/jpeg" });
        const dt = new DataTransfer();
        dt.items.add(file);

        const form = document.createElement("form");
        form.method = "post";
        form.action = "/users/avatar";
        form.enctype = "multipart/form-data";

        const methodInput = document.createElement("input");
        methodInput.type = "hidden";
        methodInput.name = "_method";
        methodInput.value = "patch";
        form.appendChild(methodInput);

        const csrfInput = document.createElement("input");
        csrfInput.type = "hidden";
        csrfInput.name = "authenticity_token";
        csrfInput.value = csrfToken;
        form.appendChild(csrfInput);

        const fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.name = "avatar";
        fileInput.files = dt.files;
        fileInput.style.display = "none";
        form.appendChild(fileInput);

        document.body.appendChild(form);
        form.submit();
      }, "image/jpeg", 0.92);
    };

    return {
      cropArea,
      imgEl,
      imgStyle,
      zoom,
      loaded,
      saving,
      errorMessage,
      onImgLoad,
      onDragStart,
      save,
    };
  },
};
</script>

<style scoped>
.crop-area {
  position: relative;
  width: 280px;
  height: 280px;
  overflow: hidden;
  margin: 0 auto;
  background: #222;
  border-radius: 4px;
}

.crop-image {
  pointer-events: none;
  max-width: none;
}

.crop-circle {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55);
  pointer-events: none;
}

.zoom-slider {
  flex: 1;
  width: 100%;
  accent-color: #f5a623;
}
</style>
